import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Trash2, Upload, FileText, Save, X, Eye, EyeOff, Download, UploadCloud } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { id: null, title: '', meta_description: '', body: '', featured_image_path: null, slug: '', published: true };

export default function ManageBlogs({ token }) {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/blogs`, auth);
      setBlogs(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error('બ્લોગ લોડ કરવામાં નિષ્ફળ (Failed to load blogs)'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startEdit = (b) => setForm({
    id: b.id, title: b.title || '', meta_description: b.meta_description || '',
    body: b.body || '', featured_image_path: b.featured_image_path || null, slug: b.slug || '',
    published: b.published !== false,
  });
  const resetForm = () => setForm(emptyForm);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image > 5MB (5MB થી ઓછી ફાઇલ)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/blogs/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, featured_image_path: res.data.path }));
      toast.success('Image uploaded');
    } catch (err) { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title જરૂરી છે (Title required)'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        meta_description: form.meta_description,
        body: form.body,
        featured_image_path: form.featured_image_path,
        slug: form.slug || undefined,
        published: form.published,
      };
      if (form.id) {
        await axios.put(`${API_URL}/api/admin/blogs/${form.id}`, payload, auth);
        toast.success('બ્લોગ અપડેટ થયો (Blog updated)');
      } else {
        await axios.post(`${API_URL}/api/admin/blogs`, payload, auth);
        toast.success('બ્લોગ ઉમેર્યો (Blog created)');
      }
      resetForm();
      await load();
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog? (કાયમી હટાવવો?)')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blogs/${id}`, auth);
      toast.success('Deleted');
      await load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const imageUrl = (p) => p ? `${API_URL}/api/public/files/${p}` : '';

  // ===== Bulk Import =====
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const sampleCSV = `title,meta_description,body,slug,featured_image_url
"પાનકાર્ડ ઓનલાઈન અરજી કેવી રીતે કરવી","સ્ટેપ-બાય-સ્ટેપ માર્ગદર્શન ગુજરાતીમાં","1. NSDL વેબસાઈટ પર જાઓ...\\n2. ફોર્મ 49A પસંદ કરો...",,
"આધારકાર્ડ સુધારો કરવાની પ્રક્રિયા","નામ / સરનામું / મોબાઈલ સુધારો કેવી રીતે કરવો","UIDAI SSUP પોર્ટલ પર જઈને ડોક્યુમેન્ટ અપલોડ કરો...",,
"GST નોંધણી માટે જરૂરી દસ્તાવેજો","GST રજિસ્ટ્રેશન માટે જરૂરી પુરાવા","PAN, આધાર, બિઝનેસ પ્રૂફ, બેન્ક વિગત...",,`;

  const downloadSample = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'kagajbook-blogs-sample.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleBulkImport = async () => {
    if (!bulkFile) { toast.error('Select a CSV file first'); return; }
    setBulkImporting(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const res = await axios.post(`${API_URL}/api/admin/blogs/bulk-import`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(res.data);
      toast.success(`Imported ${res.data.created} blogs`);
      setBulkFile(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk import failed');
    }
    setBulkImporting(false);
  };

  return (
    <div className="space-y-6" data-testid="blogs-tab">
      {/* Bulk Import */}
      <div className="bg-gradient-to-br from-[#172554] to-[#0f1a3a] rounded-xl shadow-sm p-6 text-white" data-testid="bulk-import-card">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#F5B800] text-[#172554] flex items-center justify-center shrink-0">
            <UploadCloud className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold flex items-center gap-2">
              Bulk Import Blogs <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F5B800] text-[#172554] px-2 py-0.5 rounded-full">CSV</span>
            </h3>
            <p className="text-xs text-white/70 mt-1 text-gujarati">
              એક CSV માંથી 100+ બ્લોગ એકસાથે ઉમેરો — દરેક લેખ auto-slug, meta-tags અને sitemap માં આપોઆપ Published થશે.
            </p>
            <p className="text-[11px] text-white/60 mt-1">
              Columns: <span className="font-mono text-[#F5B800]">title</span> (required), meta_description, body, slug, featured_image_url
            </p>
          </div>
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
            <button
              onClick={downloadSample}
              className="inline-flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded-lg"
              data-testid="bulk-sample-download"
            >
              <Download className="w-3.5 h-3.5" /> Sample CSV
            </button>
            <label className="inline-flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold px-3 py-2 rounded-lg cursor-pointer">
              <Upload className="w-3.5 h-3.5" />
              <span className="truncate max-w-[140px]">{bulkFile ? bulkFile.name : 'Choose CSV'}</span>
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={e => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }} data-testid="bulk-file-input" />
            </label>
            <button
              onClick={handleBulkImport}
              disabled={!bulkFile || bulkImporting}
              className="inline-flex items-center justify-center gap-1 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] text-xs font-extrabold px-4 py-2 rounded-lg disabled:opacity-50"
              data-testid="bulk-import-btn"
            >
              <UploadCloud className="w-3.5 h-3.5" /> {bulkImporting ? 'Importing…' : 'Import Now'}
            </button>
          </div>
        </div>

        {bulkResult && (
          <div className="mt-4 bg-white/5 border border-white/10 rounded-lg p-3 text-xs" data-testid="bulk-result">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-green-300 font-bold">✓ Created: {bulkResult.created}</span>
              <span className="text-yellow-300 font-bold">⚠ Skipped: {bulkResult.skipped}</span>
              <span className="text-white/60">All imported blogs are Published and included in <span className="font-mono">/api/sitemap.xml</span></span>
            </div>
            {Array.isArray(bulkResult.errors) && bulkResult.errors.length > 0 && (
              <ul className="mt-2 space-y-0.5 max-h-24 overflow-y-auto">
                {bulkResult.errors.map((er, i) => (
                  <li key={i} className="text-red-300">Row {er.row}: {er.reason}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Editor */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <h3 className="text-lg font-bold text-[#172554] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#F5B800]" /> {form.id ? 'Edit Blog' : 'Write New Blog'}
          </h3>
          {form.id && (
            <button onClick={resetForm} className="text-xs font-bold text-[#64748B] hover:text-[#172554] flex items-center gap-1">
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#172554] mb-1">Title (શીર્ષક) *</label>
              <input
                type="text" value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. પાનકાર્ડ કેવી રીતે અરજી કરવી"
                className="w-full border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] text-gujarati"
                data-testid="blog-title-input"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#172554] mb-1">Slug (URL friendly, auto if blank)</label>
              <input
                type="text" value={form.slug}
                onChange={e => setForm({ ...form, slug: e.target.value })}
                placeholder="auto-generated-from-title"
                className="w-full border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] font-mono"
                data-testid="blog-slug-input"
              />
              <p className="text-[10px] text-[#64748B] mt-1">Final URL: <span className="font-mono">/blogs/{form.slug || '<auto>'}</span></p>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#172554] mb-1">Meta Description (SEO) — max 160 chars</label>
              <textarea
                value={form.meta_description}
                onChange={e => setForm({ ...form, meta_description: e.target.value.slice(0, 160) })}
                rows={2}
                placeholder="Short SEO-friendly summary of the article (shown by Google)"
                className="w-full border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] text-gujarati"
                data-testid="blog-meta-input"
              />
              <p className="text-[10px] text-[#64748B] mt-1">{form.meta_description.length}/160</p>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#172554] mb-1">Body (લેખનું મુખ્ય લખાણ)</label>
              <textarea
                value={form.body}
                onChange={e => setForm({ ...form, body: e.target.value })}
                rows={14}
                placeholder="Write your blog post here (plain text / basic formatting supported)…"
                className="w-full border border-gray-300 rounded p-2.5 text-sm outline-none focus:border-[#172554] text-gujarati leading-relaxed"
                data-testid="blog-body-input"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#172554] mb-1">Featured Image</label>
              {form.featured_image_path ? (
                <div className="relative">
                  <img src={imageUrl(form.featured_image_path)} alt="featured" className="w-full aspect-[16/9] object-cover rounded border" />
                  <button
                    onClick={() => setForm({ ...form, featured_image_path: null })}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600"
                    data-testid="blog-remove-image"
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <label className={`cursor-pointer flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${uploading ? 'border-gray-200 opacity-60' : 'border-gray-300 hover:border-[#F5B800] hover:bg-[#F5B800]/5'}`}>
                  <Upload className="w-6 h-6 text-[#64748B]" />
                  <span className="text-xs font-bold text-[#172554]">{uploading ? 'Uploading…' : 'Upload Image'}</span>
                  <span className="text-[10px] text-[#64748B]">JPG / PNG / WEBP · max 5 MB</span>
                  <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="blog-image-input" />
                </label>
              )}
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox" checked={form.published}
                  onChange={e => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4 accent-[#F5B800]"
                  data-testid="blog-published-toggle"
                />
                <span className="text-sm font-bold text-[#172554]">Publish</span>
              </label>
              <p className="text-[10px] text-[#64748B] mt-1">Unpublished blogs are hidden from /blogs and sitemap.xml</p>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#172554] text-white font-bold py-3 rounded-lg hover:bg-[#0f1a3a] disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="blog-save-btn"
            >
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : (form.id ? 'Update Blog' : 'Publish Blog')}
            </button>
          </div>
        </div>
      </div>

      {/* Existing */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h3 className="text-lg font-bold text-[#172554]">Existing Blogs ({blogs.length})</h3>
          <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs font-bold text-[#172554] hover:text-[#F5B800]" data-testid="blog-sitemap-link">
            View sitemap.xml →
          </a>
        </div>
        {blogs.length === 0 ? (
          <div className="text-center py-10 text-[#64748B] text-gujarati">હજુ કોઈ બ્લોગ ઉમેર્યો નથી</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {blogs.map(b => (
              <div key={b.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow" data-testid={`blog-row-${b.id}`}>
                {b.featured_image_path ? (
                  <img src={imageUrl(b.featured_image_path)} alt={b.title} className="w-full aspect-[16/9] object-cover" />
                ) : (
                  <div className="w-full aspect-[16/9] bg-gradient-to-br from-[#172554] to-[#0f1a3a] flex items-center justify-center">
                    <FileText className="w-8 h-8 text-[#F5B800]/60" />
                  </div>
                )}
                <div className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    {b.published ? (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded uppercase tracking-wider"><Eye className="w-3 h-3" /> Live</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold text-[#64748B] bg-gray-100 px-1.5 py-0.5 rounded uppercase tracking-wider"><EyeOff className="w-3 h-3" /> Draft</span>
                    )}
                    <span className="text-[10px] text-[#64748B] font-mono truncate">/blogs/{b.slug}</span>
                  </div>
                  <h4 className="font-bold text-sm text-[#172554] text-gujarati line-clamp-2">{b.title}</h4>
                  <p className="text-xs text-[#64748B] text-gujarati line-clamp-2 mt-1">{b.meta_description}</p>
                  <div className="flex justify-end gap-1 mt-2 pt-2 border-t border-gray-100">
                    <a href={`/blogs/${b.slug}`} target="_blank" rel="noreferrer" className="text-[#172554] hover:bg-gray-100 p-1.5 rounded" title="View live">
                      <Eye className="w-4 h-4" />
                    </a>
                    <button onClick={() => startEdit(b)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded" data-testid={`blog-edit-${b.id}`}><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(b.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded" data-testid={`blog-delete-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
